import { LitElement, css, html, svg } from "lit";
import { customElement, property } from "lit/decorators.js";

type SourceState = "loading" | "ready" | "error";

export function resultCounts(runs: readonly { status?: unknown }[]) {
  return runs.reduce(
    (counts, run) => {
      if (run.status === "success") counts.success++;
      else if (run.status === "failed") counts.failed++;
      else counts.other++;
      return counts;
    },
    { success: 0, failed: 0, other: 0 },
  );
}

@customElement("overview-metrics")
export class OverviewMetrics extends LitElement {
  @property({ attribute: false }) counts: Record<string, number> = {};
  @property({ attribute: false }) runs: { status?: unknown }[] = [];
  @property() language = "zh-CN";
  @property() knowledgeState: SourceState = "loading";
  @property() runsState: SourceState = "loading";

  static styles = css`
    :host {
      display: block;
      margin: 18px 0;
      color: #111;
      font:
        13px/1.45 Arial,
        sans-serif;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(100%, 330px), 1fr));
      gap: 18px;
    }
    article {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      max-height: max(0px, calc(100vh - 100px));
      max-height: max(0px, calc(100dvh - 100px));
      min-width: 0;
      padding: 3px;
      background: #c0c0c0;
      border: 2px solid;
      border-color: #fff #404040 #404040 #fff;
      box-shadow: 1px 1px #000;
    }
    h2 {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 5px 8px;
      background: #000080;
      color: white;
      font-size: 14px;
    }
    .icon {
      width: 22px;
      height: 22px;
      flex: 0 0 auto;
      image-rendering: pixelated;
    }
    .body {
      min-height: 0;
      overflow: auto;
      padding: 14px;
    }
    .chart {
      background: #fff;
      border: 2px solid;
      border-color: #808080 #fff #fff #808080;
      padding: 12px;
    }
    .row {
      display: grid;
      grid-template-columns: 5em minmax(20px, 1fr) max-content;
      align-items: center;
      gap: 10px;
      margin: 9px 0;
    }
    .track {
      height: 14px;
      background: #e5e5e5;
      border: 1px solid #888;
    }
    .bar {
      height: 100%;
      background: repeating-linear-gradient(
        90deg,
        #000080 0 7px,
        #7393b3 7px 8px
      );
    }
    .number {
      font-variant-numeric: tabular-nums;
      font-weight: bold;
    }
    .note {
      margin: 10px 0 0;
      color: #333;
      font-size: 12px;
    }
    .gauge {
      display: block;
      width: min(100%, 260px);
      margin: 0 auto;
    }
    .legend {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px 16px;
      margin-top: 8px;
    }
    .legend span {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
    .swatch {
      width: 12px;
      height: 12px;
      border: 1px solid #444;
    }
    .empty {
      padding: 30px 12px;
      margin: 0;
      text-align: center;
      background: white;
    }
  `;

  private text(zh: string, en: string) {
    return this.language === "en" ? en : zh;
  }

  private status(state: SourceState) {
    return html`<p class="empty" role="status" aria-busy=${state === "loading"}>
      ${
        state === "loading"
          ? this.text("正在读取统计…", "Loading statistics…")
          : this.text("此项统计暂不可用", "These statistics are unavailable")
      }
    </p>`;
  }

  private knowledge() {
    if (this.knowledgeState !== "ready")
      return this.status(this.knowledgeState);
    const rows = [
      ["file", this.text("文件", "Files")],
      ["symbol", this.text("符号", "Symbols")],
      ["contract", this.text("契约", "Contracts")],
      ["evidence", this.text("证据", "Evidence")],
      ["chunk", this.text("内容分块", "Chunks")],
    ].map(([key, label]) => ({
      label,
      value: Number.isFinite(this.counts[key])
        ? Math.max(0, this.counts[key])
        : null,
    }));
    const maximum = Math.max(0, ...rows.map((row) => row.value ?? 0));
    return html`<div class="chart">
        ${rows.map(
          (row) =>
            html`<div class="row">
              <span>${row.label}</span>
              <div class="track" aria-hidden="true">
                <div
                  class="bar"
                  style=${`width: ${maximum ? ((row.value ?? 0) / maximum) * 100 : 0}%`}
                ></div>
              </div>
              <span class="number"
                >${row.value?.toLocaleString(this.language) ?? "—"}</span
              >
            </div>`,
        )}
      </div>
      ${
        rows.every((row) => row.value === 0)
          ? html`<p class="note">
              ${this.text("这些类别暂无知识记录。", "No knowledge records in these categories.")}
            </p>`
          : null
      }
      <p class="note">
        ${this.text(
          "按各类记录数比较，条宽以最大项为基准；各类记录有关联，不代表去重总量。",
          "Record counts by type, scaled to the largest category. Related categories are not a deduplicated total.",
        )}
      </p>`;
  }

  private results() {
    if (this.runsState !== "ready") return this.status(this.runsState);
    const total = this.runs.length;
    if (!total)
      return html`<p class="empty">
        ${this.text("暂无执行记录", "No execution records")}
      </p>`;
    const counts = resultCounts(this.runs);
    let offset = 0;
    const segments = [
      {
        label: this.text("成功", "Success"),
        count: counts.success,
        color: "#008080",
      },
      {
        label: this.text("失败", "Failed"),
        count: counts.failed,
        color: "#a02020",
      },
      {
        label: this.text("其他状态", "Other states"),
        count: counts.other,
        color: "#808000",
      },
    ].map((item) => {
      const segment = {
        ...item,
        start: offset,
        size: (item.count / total) * 100,
      };
      offset += segment.size;
      return segment;
    });
    return html`<div class="chart">
        <svg class="gauge" viewBox="0 0 200 125" aria-hidden="true">
          <path
            d="M 30 95 A 70 70 0 0 1 170 95"
            fill="none"
            stroke="#ddd"
            stroke-width="20"
          />
          ${segments.map(
            (item) => svg`<path
            d="M 30 95 A 70 70 0 0 1 170 95" pathLength="100"
            fill="none" stroke=${item.color} stroke-width="20"
            stroke-dasharray=${`${item.size} ${100 - item.size}`}
            stroke-dashoffset=${-item.start}
          />`,
          )}
          <text
            x="100"
            y="86"
            text-anchor="middle"
            font-size="34"
            font-weight="bold"
          >
            ${total}
          </text>
          <text x="100" y="113" text-anchor="middle" font-size="12">
            ${this.text("条最近记录", "recent records")}
          </text>
        </svg>
        <div class="legend">
          ${segments.map(
            (item) =>
              html`<span>
                <i
                  class="swatch"
                  style=${`background: ${item.color}`}
                  aria-hidden="true"
                ></i>
                ${item.label} <b class="number">${item.count}</b>
              </span>`,
          )}
        </div>
      </div>
      <p class="note">
        ${this.text(
          `仅展示最近 ${total} 条（最多 5 条）Codex 记录，非全站成功率；运行中及未知状态计入“其他状态”。`,
          `Latest ${total} Codex records (up to 5), not the overall success rate. Running and unknown statuses are grouped as other states.`,
        )}
      </p>`;
  }

  render() {
    return html`<div class="grid">
      <article>
        <h2>
          <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#fff" stroke="#000" d="M2 2h20v20H2z" />
            <path
              fill="#008080"
              stroke="#000"
              d="M5 12h3v7H5zM10 8h3v11h-3zM15 4h3v15h-3z"
            />
          </svg>
          ${this.text("知识记录分布", "Knowledge records")}
        </h2>
        <div class="body">${this.knowledge()}</div>
      </article>
      <article>
        <h2>
          <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#ddd" stroke="#000" d="M2 19a10 10 0 0 1 20 0z" />
            <path stroke="#800000" stroke-width="2" d="m12 18 5-9" />
            <path fill="#000" d="M10 17h4v3h-4z" />
          </svg>
          ${this.text("最近执行仪表", "Recent execution monitor")}
        </h2>
        <div class="body">${this.results()}</div>
      </article>
    </div>`;
  }
}
